import React, { useState } from 'react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import PhoneField from 'components/v2/forms/PhoneField';
import { addWarehouseUser } from 'utils/UserWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import validator from 'email-validator';
import CargomaticInput from 'components/v2/input';
import FormCard from 'components/v2/FormCard';

const getEmptyState = () => {
  return {
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    phone: '',
  };
};

const RegisterWarehouseUser = () => {
  const [warehouseUser, setWarehouseUser] = useState(getEmptyState());
  const { t } = useTranslation();
  const handlePhoneChange = (phone) => {
    setWarehouseUser((prev) => ({ ...prev, phone }));
  };

  const handleChange = (field) => (value) => {
    setWarehouseUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectAddress = (location) => {
    const { address } = location;
    setWarehouseUser((prev) => ({ ...prev, location, address }));
  };

  const handleAddWarehouseUser = async (user) => {
    try {
      const result = await addWarehouseUser(user);
      setWarehouseUser(getEmptyState());
      return result;
    } catch (e) {
      return e;
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const name = `${warehouseUser.first_name} ${warehouseUser.last_name}`;
    const optimized_name = name.toUpperCase();
    const first_name = warehouseUser.first_name.trim();
    const last_name = warehouseUser.last_name.trim();
    const payload = {
      ...warehouseUser,
      first_name,
      last_name,
      name,
      optimized_name,
    };
    const response = await handleAddWarehouseUser(payload);
    const {
      entity: { success = false },
    } = response;
    if (!success) {
      const errorMessage =
        typeof response.entity.error === 'string'
          ? response.entity.error
          : t('ERROR_OCCURRED');
      showToast(errorMessage, errorMessage, 'error');
    } else {
      showToast(
        t('NEW_WAREHOUSE_USER_ADDED'),
        t('NEW_WAREHOUSE_USER_ADDED'),
        'success',
      );
    }
  };
  const disabled =
    isEmpty(warehouseUser.email) ||
    isEmpty(warehouseUser.first_name) ||
    isEmpty(warehouseUser.last_name) ||
    isEmpty(warehouseUser.password) ||
    isEmpty(warehouseUser.phone) ||
    !validator.validate(warehouseUser.email);

  return (
    <FormCard
      actions={() => (
        <button
          data-testid="warehouse-user-register-button"
          disabled={disabled}
          className="button button-green"
          type="submit"
          onClick={handleRegister}
        >
          {t('REGISTER_WAREHOUSE_USER')}
        </button>
      )}
    >
      <div data-testid="warehouse-user-register-page" className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 leading-6">
          {t('WAREHOUSE_USER_PROFILE')}
        </h3>
      </div>
      <div className="grid grid-cols-4 gap-6">
        <div
          data-testid="warehouse-user-first-name"
          className="col-span-4 sm:col-span-2"
        >
          <CargomaticInput
            className="mb-4"
            type="text"
            label={t('FIRST_NAME')}
            placeholder="John"
            onChange={handleChange('first_name')}
            value={warehouseUser.first_name}
          />
        </div>
        <div
          data-testid="warehouse-user-last-name"
          className="col-span-4 sm:col-span-2"
        >
          <CargomaticInput
            className="mb-4"
            type="text"
            label={t('LAST_NAME')}
            placeholder="Smith"
            onChange={handleChange('last_name')}
            value={warehouseUser.last_name}
          />
        </div>
        <div
          data-testid="warehouse-user-phone"
          className="col-span-4 sm:col-span-2"
        >
          <label className="block mb-2 form-label">{t('PHONE')}</label>
          <PhoneField
            id="warehouse-user-phone"
            onChange={handlePhoneChange}
            value={warehouseUser.phone}
          />
        </div>
        <div
          data-testid="warehouse-user-email"
          className="col-span-4 sm:col-span-2"
        >
          <CargomaticInput
            className="mb-4"
            type="email"
            label={t('EMAIL')}
            placeholder="example@cargomatic.com"
            onChange={handleChange('email')}
            value={warehouseUser.email}
          />
        </div>
        <div
          data-testid="warehouse-user-password"
          className="col-span-4 sm:col-span-2"
        >
          <CargomaticInput
            className="mb-4"
            type="password"
            label={t('PASSWORD')}
            placeholder={t('PASSWORD')}
            onChange={handleChange('password')}
            value={warehouseUser.password}
          />
        </div>
      </div>
    </FormCard>
  );
};

export default RegisterWarehouseUser;
